import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

function Section({ number, title, children }) {
  return (
    <div className="flex space-x-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-vextaris-purple/10 border border-vextaris-purple/30 flex items-center justify-center text-vextaris-purple text-sm font-medium">
        {number}
      </div>
      <div className="flex-1 pb-8">
        <h3 className="text-white font-medium mb-2">{title}</h3>
        <div className="text-gray-400 text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  );
}

export default function VipsGuide() {
  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/guide" className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Guide
      </Link>
      <h1 className="text-3xl font-bold text-white mb-8">Building VIPs (Vextaris Intent Programs)</h1>

      <p className="text-gray-400 leading-relaxed mb-10">
        VIPs are declarative JSON programs that compose multiple native operations into a single,
        reusable on-chain routine. There is no bytecode, no compiler, no VM in the traditional sense
        — you define what should happen, the chain executes it atomically.
      </p>

      <div className="space-y-2">
        <Section number={1} title="What is a VIP">
          <p>
            A VIP is a JSON document with a <span className="text-white">namespace</span>,
            a <span className="text-white">name</span>, typed <span className="text-white">parameters</span>,
            and a list of <span className="text-white">steps</span>. Each step maps to a native Vextaris
            operation (transfer, swap, mint, burn, etc.). When someone invokes a VIP, all steps execute
            atomically — either everything succeeds or nothing does.
          </p>
          <p>
            VIPs are stored on-chain and indexed by <span className="text-white">namespace/name</span>.
            Anyone can invoke a registered VIP by passing the required parameters.
          </p>
        </Section>

        <Section number={2} title="Open Intent Studio">
          <p>
            Go to <Link to="/intent-studio" className="text-vextaris-blue hover:underline">Intent Studio</Link> and
            select the <span className="text-white">Register</span> tab. This is where you author and register
            new VIPs.
          </p>
        </Section>

        <Section number={3} title="Load a template">
          <p>
            Click <span className="text-white">Load Template</span> to populate the editor with a working
            VIP structure. This gives you the correct JSON shape to start from. Templates include common
            patterns like "airdrop to list" and "swap and transfer."
          </p>
        </Section>

        <Section number={4} title="Define parameters">
          <p>
            Parameters are the inputs your VIP accepts when invoked. Each parameter has
            a <span className="text-white">name</span> and a <span className="text-white">type</span>.
          </p>
          <p>Supported types:</p>
          <div className="bg-black/30 rounded-lg p-3 font-mono text-xs text-gray-300 mt-1">
            String, Address, Amount, U8, U64, Bool, Vec&lt;Address&gt;, Vec&lt;Amount&gt;
          </div>
          <p>
            Parameters are referenced in steps using template
            expressions: <code className="text-vextaris-blue">{"${params.recipient}"}</code>.
          </p>
        </Section>

        <Section number={5} title="Define steps">
          <p>
            Each step is a native operation. Steps can reference the sender, parameters, and results
            from previous steps using template expressions:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li><code className="text-vextaris-blue">{"${sender}"}</code> — the address invoking the VIP</li>
            <li><code className="text-vextaris-blue">{"${params.x}"}</code> — a declared parameter</li>
            <li><code className="text-vextaris-blue">{"${steps.0.result.mint}"}</code> — output from a previous step</li>
          </ul>
          <p>
            Steps execute in order. If any step fails, the entire VIP reverts.
          </p>
        </Section>

        <Section number={6} title="Simulate first">
          <p>
            Switch to the <span className="text-white">Simulate</span> tab and dry-run your VIP with test
            parameters. Simulation shows you exactly what operations would execute, what state changes
            would occur, and whether any step would fail. Always simulate before registering.
          </p>
        </Section>

        <Section number={7} title="Register the VIP">
          <p>
            Registration costs <span className="text-white">50 VXS</span>. When you register, the chain runs
            a static safety analysis on your VIP and assigns a safety score. The VIP is then available
            on-chain at your <span className="text-white">namespace/name</span> path.
          </p>
          <p>
            After registration, check your VIP's safety score. Low scores (due to patterns like
            unconstrained minting or hardcoded recipient addresses) may discourage users from invoking it.
          </p>
        </Section>

        <Section number={8} title="Others invoke your VIP">
          <p>
            Once registered, anyone can invoke your VIP by
            specifying <span className="text-white">namespace/name</span> and providing the required parameters.
            Invocations go through the same safety checks and execute atomically.
          </p>
        </Section>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-vextaris-darker border border-gray-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-3">Tips</h3>
        <ul className="text-gray-400 text-sm space-y-2 list-disc list-inside">
          <li>Keep step count low — under <span className="text-white">10 steps</span> is ideal for gas efficiency and readability.</li>
          <li>Use <code className="text-vextaris-blue">{"${sender}"}</code> for recipient addresses. Never hardcode addresses in steps.</li>
          <li>Always simulate before registering. Registration is permanent and costs 50 VXS.</li>
          <li>Check the safety score after registration. Refactor if the score is low.</li>
          <li>VIPs can call other VIPs — compose complex workflows from simple building blocks.</li>
        </ul>
      </div>

      {/* Docs link */}
      <div className="mt-6 text-sm text-gray-500">
        For the full VIP specification, see{' '}
        <a href="https://docs.vextaris.io/architecture/vip" target="_blank" rel="noopener noreferrer" className="text-vextaris-blue hover:underline">
          docs.vextaris.io/architecture/vip
        </a>.
      </div>
    </div>
  );
}
